import { Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Data, NavigationEnd, NavigationStart, NavigationError, Router, NavigationCancel, NavigationSkipped, ActivationEnd, ResolveEnd } from '@angular/router';
import { AuthenticationState, FullPageMaskService, ISecurityEvent, SecurityEvents, SecurityService } from 'common';
import { BannerDataService, BannerSize, IBannerData, MenuService, NavigationItemChild } from 'root-layout';
import { HeaderNavigationService } from './services/header-navigation.service';
import { ProfileService } from './services/profile.service';
import { RoleService } from './services/role.service';
import { Role } from './interfaces/role.interface';
import { RouteHistoryService } from './services/route-history.service';
import { MobileWarningService } from './services/mobile-warning.service';
import { filter, map, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'si-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class AppComponent implements OnDestroy, OnInit {
  title = 'Sender ID Registry';
  loggingIn = false;
  loggedIn = false;
  providerType = '';
  companyName = 'Log In';
  externalUrl = '/';
  showLogInBackground: boolean = false;
  banner: IBannerData;
  bannerSize = BannerSize;

  menuItems: {title: string; url: string}[] = [];
  qsSubscription: Subscription;

  private currentTheme = 'theme-industry';
  private previousUrl: string;
  private currentUrl: string;

  constructor(
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private bannerDataService: BannerDataService,
    private menuService: MenuService,
    private security: SecurityService,
    private maskService: FullPageMaskService,
    private headerNavigationService: HeaderNavigationService,
    private profileService: ProfileService,
    private roleService: RoleService,
    private routeHistory: RouteHistoryService,
    private mobileWarningService: MobileWarningService
  ) {
    this.banner = { title: '', subtitle: '', size: BannerSize.medium, showButtons: true };

    router.events.subscribe((routerEvent) => {
      if (routerEvent instanceof NavigationStart) {
        this.maskService.showLoading();
      }
      if (
        routerEvent instanceof NavigationError ||
        routerEvent instanceof NavigationSkipped ||
        routerEvent instanceof NavigationCancel ||
        routerEvent instanceof ActivationEnd ||
        routerEvent instanceof ResolveEnd
      ) {
        if (this.maskService.isVisible) {
          this.maskService.hideLoading();
        }
      }
      if (routerEvent instanceof NavigationEnd) {
        this.maskService.hideLoading();
        this.previousUrl = this.currentUrl;
        this.currentUrl = routerEvent.url;
        this.changeBannerText(route.root.firstChild?.snapshot.data);
        this.showLogInBackground = this.currentUrl.indexOf('/accounts/login') >= 0;
      }
    });

    security.userEvents.subscribe((event: ISecurityEvent) => {
      if (event?.eventType === SecurityEvents.accountVerified) {
        const user = this.security.user;

        this.loggingIn = false; 
        if (user !== null) {
          this.loggedIn = true;
          this.companyName = this.security.profile.user?.username ?? 'NO USER NAME!';
          this.providerType = this.security.profile.selectedAccount?.providerTypeString ?? 'NO ACCOUNT SELECTED!';
          this.setMenuItems();
        } else if (this.security.authenticationState === AuthenticationState.unauthorised && this.security.profile.selectedAccount) {
          this.security.completeLogout();
        }
      } else {
        const securityStates = [SecurityEvents.badCredentials, SecurityEvents.sourceApplicationChanged];
        if (event !== null && !securityStates.includes(event?.eventType) && this.security.authenticationState !== AuthenticationState.accountVerified) {
          this.loggedIn = false;
          this.loggingIn = true;
        } else if (this.security.authenticationState === AuthenticationState.unauthorised && this.security.user) {
          this.security.completeLogout();
        }
      }
    });
  }

  ngOnInit(): void {
    this.mobileWarningService.checkAndShowMobileWarning();
    this.qsSubscription = this.router.events
    .pipe(filter(e => e instanceof NavigationEnd))
    .subscribe(() => {
      const params = this.router.routerState.snapshot.root.queryParams;
      if (params['reason'] === 'logout') {
        this.logout();
      }
    });
  }

  ngOnDestroy(): void {
    this.qsSubscription?.unsubscribe();
    this.security.userEvents.unsubscribe();
    this.menuItems = [];
  }

  get accountName() {
    return this.profileService.accountName;
  }

  get roleName() {
    switch (this.roleService.role) {
      case Role.PaOrMsp:
        return 'PA or MSP';
      default:
        return this.roleService.role?.toUpperCase();
    }
  }

  get isAdmin() {
    return this.roleService.isAdmin;
  }

  navigateHome(): void {
    this.router.navigate(['/']);
  }

  async loadProfileSidenav(event): Promise<void> {
    const route = 'profile';
    const outlet = 'profilePopup';

    (event as Event).preventDefault();
    const calculatedRoute = [];

    const outletPath = {};
    outletPath[outlet] = [route];
    calculatedRoute.push({ outlets: outletPath });

    await this.router.navigate(calculatedRoute, { skipLocationChange: true });
  }

  logout(): void {
    this.security.logout();
  }

  changeBannerText(data: Data) {
    if (data?.bannerKey) {
      this.banner = this.bannerDataService.bannerDataByKey(data.bannerKey);
    }
  }

  switchTheme() {
    this.renderer.removeClass(document.body, this.currentTheme);
    const selectedTheme: string = this.currentTheme === 'theme-industry' ? 'theme-consumer' : 'theme-industry';

    this.renderer.addClass(document.body, selectedTheme);
    this.currentTheme = selectedTheme;

    this.router.navigate([this.previousUrl]);
  }

  handleToggleSidenav(event: MouseEvent, profileSidenav: MatSidenav) {
    profileSidenav.toggle();
    this.loadProfileSidenav(event)
  }

  setMenuItems() {
    const items = this.headerNavigationService.getNavItems();
    this.menuService.setMenu(items);
    this.menuItems = Object.entries(items).map(([key, value]) => ({
      title: key,
      url: value[0].route,
    }));
  }
}
